import { DOCUMENT, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { GuideStepDirective } from '../guide-step.directive';
import { GuideStep } from '../guide-step.enum';
import { GuideService } from '../guide.service';

@Component({
  selector: 'app-guide-overlay',
  imports: [MatButtonModule, MatIconModule, NgOptimizedImage, GuideStepDirective],
  template: `
    @if (guideService.isActive()) {
      <!-- Backdrop: z-index 1001 → above CDK overlay container (1000) covering bottomsheet -->
      <div
        class="fixed inset-0 z-1001 bg-black/35 pointer-events-auto"
        style="animation: fadeIn 0.4s ease-in-out forwards;"
        aria-hidden="true"
      ></div>

      <!-- Mock parking marker at viewport center during MAP_POINT step -->
      @if (guideService.currentStep() === guideStep.MAP_POINT) {
        <div
          class="fixed top-[38%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-1002 flex items-center justify-center cursor-default"
          style="animation: fadeIn 0.4s ease-out forwards;"
          [appGuideStep]="guideStep.MAP_POINT"
          aria-hidden="true"
        >
          <img ngSrc="/icons/parking-free-poi.svg" width="30" height="30" alt="punkt parkingu" />
          <div
            class="absolute -inset-2 rounded-lg border-2 border-[#1098f7]/60 animate-[guide-pulse_1.6s_ease-out_infinite]"
          ></div>
        </div>
      }

      <!-- Tooltip: z-index 1002 → above backdrop, rendered here (not in CDK) -->
      @if (tooltipStyle() !== null) {
        <div
          class="guide-tooltip fixed z-1002 bg-white text-[#242424] rounded-[10px] px-[14px] py-[10px] text-[13px] leading-[1.4] max-w-[220px] shadow-[0_4px_16px_rgba(0,0,0,0.18)] pointer-events-none"
          style="border: 3px solid #1098f7;"
          [class]="tooltipArrowClass()"
          [style]="tooltipStyle()"
          animate.enter="tooltip-enter"
          animate.leave="tooltip-leave"
          role="tooltip"
        >
          {{ activeLabel() }}
        </div>
      }

      <!-- Nav: z-index 1003 -->
      <div
        class="fixed left-1/2 -translate-x-1/2 z-1003 flex flex-col items-center gap-[30px] py-4 px-5 bg-white/30 backdrop-blur-xs border-2 border-(--par-color-primary) rounded-[20px] pointer-events-auto transition-[top,bottom] duration-300 ease-out"
        style="animation: guide-slide-up 0.4s ease-out 0.15s both;"
        [class]="isSheetStep ? 'top-[35%] bottom-auto' : 'bottom-[35%] top-auto'"
        role="dialog"
        aria-label="Interaktywny przewodnik"
      >
        <div class="flex items-center justify-between w-full gap-4">
          <div class="par-font-small text-white tracking-[0.5px]">
            Krok {{ currentIndex + 1 }} z {{ guideService.steps.length }}
          </div>
          <button
            class="bg-none border-none text-white/70 cursor-pointer p-0 flex items-center leading-none hover:text-white transition-colors"
            (click)="guideService.stop()"
            aria-label="Zamknij przewodnik"
          >
            <mat-icon class="text-[20px]! w-[20px]! h-[20px]!">close</mat-icon>
          </button>
        </div>

        <div class="flex gap-[12px] items-center">
          <button
            mat-stroked-button
            class="guide-btn-back"
            (click)="guideService.prev()"
            aria-label="Poprzedni krok"
          >
            <mat-icon>arrow_back</mat-icon>
            Wróć
          </button>

          @if (!guideService.isLastStep) {
            <div class="guide-btn-pulse">
              <button
                mat-flat-button
                class="guide-btn-next"
                (click)="guideService.next()"
                aria-label="Następny krok"
              >
                Dalej
                <mat-icon>arrow_forward</mat-icon>
              </button>
            </div>
          } @else {
            <div class="guide-btn-pulse">
              <button
                mat-flat-button
                class="guide-btn-next"
                (click)="guideService.complete()"
                aria-label="Zakończ przewodnik"
              >
                Zakończ
                <mat-icon>check</mat-icon>
              </button>
            </div>
          }
        </div>
      </div>
    }
  `,
  styles: `
    :host {
      display: contents;
    }

    /* z-index stack:
       1001 = backdrop (above CDK overlay container at 1000 → covers bottomsheet)
       1002 = tooltip  (above backdrop, rendered here not in CDK)
       1003 = nav buttons (topmost interactive layer)
    */

    @keyframes guide-pulse {
      0% {
        transform: scale(1);
        opacity: 0.8;
      }
      80% {
        transform: scale(1.5);
        opacity: 0;
      }
      100% {
        transform: scale(1.5);
        opacity: 0;
      }
    }

    @keyframes guide-slide-up {
      from {
        opacity: 0;
        transform: translateX(-50%) translateY(12px);
      }
      to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }

    /* ---- Tooltip (rendered directly, not via CDK) ---- */

    /* animate.enter class – Angular adds it on element insertion */
    @keyframes tooltip-enter-kf {
      from {
        opacity: 0;
        scale: 0.92;
      }
      to {
        opacity: 1;
        scale: 1;
      }
    }

    .tooltip-enter {
      animation: tooltip-enter-kf 150ms ease-out forwards;
    }

    /* animate.leave class – Angular adds it before removal, waits for animation to finish */
    @keyframes tooltip-leave-kf {
      from {
        opacity: 1;
        scale: 1;
      }
      to {
        opacity: 0;
        scale: 0.92;
      }
    }

    .tooltip-leave {
      animation: tooltip-leave-kf 120ms ease-in forwards;
    }

    /* Arrow base */
    .guide-tooltip::after {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border-style: solid;
    }

    /* Arrow pointing DOWN (tooltip above element) */
    .arrow-bottom-start::after {
      bottom: -10px;
      left: 14px;
      border-width: 10px 8px 0 8px;
      border-color: #fff transparent transparent transparent;
      filter: drop-shadow(0 2px 1px rgba(16, 152, 247, 0.4));
    }
    .arrow-bottom-center::after {
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      border-width: 10px 8px 0 8px;
      border-color: #fff transparent transparent transparent;
      filter: drop-shadow(0 2px 1px rgba(16, 152, 247, 0.4));
    }
    .arrow-bottom-end::after {
      bottom: -10px;
      right: 14px;
      border-width: 10px 8px 0 8px;
      border-color: #fff transparent transparent transparent;
      filter: drop-shadow(0 2px 1px rgba(16, 152, 247, 0.4));
    }

    /* Arrow pointing UP (tooltip below element) */
    .arrow-top-start::after {
      top: -10px;
      left: 14px;
      border-width: 0 8px 10px 8px;
      border-color: transparent transparent #fff transparent;
      filter: drop-shadow(0 -2px 1px rgba(16, 152, 247, 0.4));
    }
    .arrow-top-center::after {
      top: -10px;
      left: 50%;
      transform: translateX(-50%);
      border-width: 0 8px 10px 8px;
      border-color: transparent transparent #fff transparent;
      filter: drop-shadow(0 -2px 1px rgba(16, 152, 247, 0.4));
    }
    .arrow-top-end::after {
      top: -10px;
      right: 14px;
      border-width: 0 8px 10px 8px;
      border-color: transparent transparent #fff transparent;
      filter: drop-shadow(0 -2px 1px rgba(16, 152, 247, 0.4));
    }

    .guide-btn-back {
      color: rgba(255, 255, 255, 0.8) !important;
      border-color: rgba(255, 255, 255, 0.8) !important;
    }

    .guide-btn-back[disabled],
    .guide-btn-back[disabled]:hover {
      opacity: 0.4 !important;
    }

    @keyframes guide-fade-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes guide-slide-up {
      from {
        opacity: 0;
        transform: translateX(-50%) translateY(12px);
      }
      to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    }

    @keyframes guide-btn-pulse-kf {
      0% {
        transform: scale(1);
      }
      15% {
        transform: scale(1.025);
      }
      30% {
        transform: scale(1);
      }
      45% {
        transform: scale(1.025);
      }
      60% {
        transform: scale(1);
      }
      100% {
        transform: scale(1);
      }
    }

    .guide-btn-pulse {
      animation: guide-btn-pulse-kf 1.2s ease-in-out infinite;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuideOverlayComponent {
  readonly guideService = inject(GuideService);
  readonly guideStep = GuideStep;
  private readonly _document = inject(DOCUMENT);

  readonly tooltipStyle = computed((): Record<string, string> | null => {
    const step = this.guideService.currentStep();
    if (!step) return null;

    const config = this.guideService.stepConfig(step);

    // Fixed position defined in config (e.g. sheet steps) – use immediately, no DOM measurement needed
    if (config.fixedStyle) {
      return config.fixedStyle;
    }

    // Dynamic position based on element rect
    const rect = this.guideService.activeElementRect();
    if (!rect) return null;

    const GAP = 12;
    const vw = this._document.documentElement.clientWidth;
    const vh = this._document.documentElement.clientHeight;
    const style: Record<string, string> = {};

    if (config.yPosition === 'bottom') {
      style['bottom'] = `${vh - rect.top + GAP}px`;
    } else {
      style['top'] = `${rect.bottom + GAP}px`;
    }

    if (config.xAlign === 'start') {
      style['left'] = `${rect.left}px`;
    } else if (config.xAlign === 'center') {
      style['left'] = `${rect.left + rect.width / 2}px`;
      style['transform'] = 'translateX(-50%)';
    } else {
      style['right'] = `${vw - rect.right}px`;
    }

    return style;
  });

  readonly tooltipArrowClass = computed((): string => {
    const step = this.guideService.currentStep();
    if (!step) return '';
    const config = this.guideService.stepConfig(step);
    return `arrow-${config.yPosition}-${config.xAlign}`;
  });

  readonly activeLabel = computed((): string => {
    const step = this.guideService.currentStep();
    if (!step) return '';
    return this.guideService.stepConfig(step).label;
  });

  get currentIndex(): number {
    const step = this.guideService.currentStep();
    if (step === null) return 0;
    return this.guideService.steps.indexOf(step);
  }

  get isSheetStep(): boolean {
    const step = this.guideService.currentStep();
    return step !== null && this.guideService.sheetSteps.has(step);
  }
}
